import { MassivePackingApiAdapter } from '../adapters';
import type {
  MassivePackingData,
  MassivePackingGroup,
  MassivePackingOrder,
  MassivePackingPreparation,
  NormalizedOrderProduct,
  PreparationBucket,
} from '../models';

/**
 * Servicio de dominio para la pantalla de Packing Masivo.
 * Encapsula toda la lógica de negocio en cliente:
 * - Carga de datos
 * - Normalización y agrupación de pedidos por composición
 */
export class MassivePackingService {
  constructor(private readonly adapter: MassivePackingApiAdapter) {}

  // 🎯 Cargar preparación + pedidos y construir grupos
  async loadMassivePackingData(
    preparationId: number,
  ): Promise<MassivePackingData> {
    const [preparation, orders] = await Promise.all([
      this.adapter.getPreparation(preparationId),
      this.adapter.getOrdersByPreparationId(preparationId),
    ]);

    const buckets = (preparation as MassivePackingPreparation & { buckets?: PreparationBucket[] }).buckets;
    const groups =
      Array.isArray(buckets) && buckets.length > 0
        ? this.buildGroupsFromBuckets(buckets, orders)
        : this.buildGroups(orders);

    return {
      preparation,
      orders,
      groups,
    };
  }

  // 🎯 Construir grupos desde buckets de la API (preparación type 1)
  private buildGroupsFromBuckets(
    buckets: PreparationBucket[],
    orders: MassivePackingOrder[],
  ): MassivePackingGroup[] {
    return buckets.map((bucket) => {
      const groupOrders = bucket.order_ids
        .map((orderId) => orders.find((o) => o.id === orderId))
        .filter((o): o is MassivePackingOrder => o != null);

      const products = bucket.items.map((item) => ({
        productId: item.product_id,
        productName: item.product_name,
        productCode: item.code,
        perOrderQuantity: item.quantity,
        totalQuantity: item.quantity * bucket.quantity_of_orders_in_group,
        imageHash: item.image_url || null,
      }));

      return {
        groupKey: `bucket-${bucket.id}`,
        orders: groupOrders.length > 0 ? groupOrders : this.buildMinimalOrdersForBucket(bucket),
        products,
      };
    });
  }

  // Cuando la API devuelve buckets pero no tenemos órdenes completas, crear órdenes mínimas para la UI
  private buildMinimalOrdersForBucket(bucket: PreparationBucket): MassivePackingOrder[] {
    const isPreparado = bucket.status === 'Preparado';
    return bucket.order_ids.map((orderId) => ({
      id: orderId,
      ref: `#${orderId}`,
      status_id: 1,
      order_created_at: '',
      shop: { id: 0, name: '' },
      customer: { name: '-' },
      shipment: { processed_at: isPreparado ? new Date().toISOString() : undefined },
      lines: [],
    })) as MassivePackingOrder[];
  }

  // 🎯 Expandir packs a productos unitarios y generar clave de agrupación
  private normalizeOrderProducts(order: MassivePackingOrder): {
    products: NormalizedOrderProduct[];
    groupKey: string;
  } {
    const map = new Map<string, NormalizedOrderProduct>();

    (order.lines || []).forEach((line: any) => {
      // Si la línea es un pack, expandir a componentes
      if (line.pack && line.pack.lines) {
        const packQty = line.qty || 1;

        line.pack.lines.forEach((packLine: any) => {
          const productId: number | undefined =
            packLine.product_id ||
            packLine.product?.id ||
            line.product_id ||
            line.product?.id;

          if (!productId) return;

          const key = String(productId);
          const existing = map.get(key);
          const increment = (packLine.qty || 0) * packQty;

          const base: NormalizedOrderProduct = existing ?? {
            productId,
            productCode:
              packLine.product?.code ||
              line.product?.code ||
              undefined,
            productName:
              packLine.product?.name ||
              line.product?.name ||
              undefined,
            perOrderQuantity: 0,
            imageHash:
              packLine.product?.image_hash ||
              line.product?.image_hash ||
              null,
          };

          base.perOrderQuantity += increment;
          map.set(key, base);
        });
        return;
      }

      // Producto individual
      const productId: number | undefined =
        line.product_id || line.product?.id;
      if (!productId) return;

      const key = String(productId);
      const existing = map.get(key);
      const increment = line.qty || 0;

      const base: NormalizedOrderProduct = existing ?? {
        productId,
        productCode: line.product?.code,
        productName: line.product?.name,
        perOrderQuantity: 0,
        imageHash: line.product?.image_hash || null,
      };

      base.perOrderQuantity += increment;
      map.set(key, base);
    });

    const products = Array.from(map.values()).sort((a, b) =>
      a.productId - b.productId,
    );

    const groupKey = products
      .map((p) => `p${p.productId}:${p.perOrderQuantity}`)
      .join('|');

    return { products, groupKey };
  }

  // 🎯 Construir grupos de pedidos por misma composición de productos
  private buildGroups(orders: MassivePackingOrder[]): MassivePackingGroup[] {
    const groupsMap = new Map<
      string,
      { orders: MassivePackingOrder[]; products: NormalizedOrderProduct[] }
    >();

    orders.forEach((order) => {
      const { products, groupKey } = this.normalizeOrderProducts(order);
      if (!groupKey) return;

      const entry = groupsMap.get(groupKey);
      if (entry) {
        entry.orders.push(order);
      } else {
        groupsMap.set(groupKey, { orders: [order], products });
      }
    });

    const groups: MassivePackingGroup[] = [];

    groupsMap.forEach(({ orders: groupOrders, products }, groupKey) => {
      const productSummaries = products.map((p) => ({
        ...p,
        totalQuantity: p.perOrderQuantity * groupOrders.length,
      }));

      groups.push({
        groupKey,
        orders: groupOrders,
        products: productSummaries,
      });
    });

    // Ordenar grupos por cantidad de pedidos (mayor primero)
    groups.sort((a, b) => b.orders.length - a.orders.length);

    return groups;
  }

  // Getter expuesto para que los hooks puedan reutilizar la lógica de agrupación si es necesario
  public getAdapter() {
    return this.adapter;
  }
}

